// frontend/src/marketing/pages/legal/TermsPage.tsx
import * as React from 'react';
import { LegalPageLayout, type LegalSection } from './LegalPageLayout';

const SECTIONS: LegalSection[] = [
  {
    heading: 'Agreement',
    body: (
      <p>
        These Terms of Service ("Terms") govern your access to and use of Worqly — the business
        operations platform available at worqly.io, including its dashboard, APIs, and desktop
        agent (together, the "Service"). By creating a workspace or using the Service you agree
        to these Terms on behalf of yourself and, where applicable, the organization you represent.
      </p>
    ),
  },
  {
    heading: 'Your workspace and account',
    body: (
      <>
        <p>
          You are responsible for the activity that occurs under your workspace, for maintaining
          the confidentiality of credentials, and for ensuring your users comply with these Terms.
          You must provide accurate signup information and keep a working billing email on file.
        </p>
        <p>You must be at least 18 years old and able to form a binding contract to use the Service.</p>
      </>
    ),
  },
  {
    heading: 'Subscriptions, trials and billing',
    body: (
      <>
        <p>
          New workspaces start on a free trial with all modules enabled; no payment method is
          required to start. After the trial, the Service is billed monthly based on your enabled
          modules and, for metered modules, the employees actually tracked or paid, as described
          on the pricing page. Prices may change with at least 30 days' notice effective from your
          next billing cycle.
        </p>
        <p>
          Fees are non-refundable except where required by law. If an invoice remains unpaid after
          reasonable notice, we may suspend the workspace until the balance is settled.
        </p>
      </>
    ),
  },
  {
    heading: 'Your data',
    body: (
      <>
        <p>
          You retain all rights to the data you and your users submit to the Service ("Customer
          Data"). We process Customer Data only to provide and improve the Service, as described
          in the <a className="underline underline-offset-2" href="/legal/privacy">Privacy Policy</a> and{' '}
          <a className="underline underline-offset-2" href="/legal/dpa">Data Processing Addendum</a>.
          Each workspace's data is logically isolated.
        </p>
        <p>
          You are responsible for having a lawful basis to store the personal data you put into the
          Service — in particular employee data used in the HR, payroll and monitoring modules, and
          for complying with local employment-law requirements (including employee notice/consent
          for screen monitoring) before enabling the monitoring module.
        </p>
      </>
    ),
  },
  {
    heading: 'Acceptable use',
    body: (
      <p>
        Use of the Service is subject to the{' '}
        <a className="underline underline-offset-2" href="/legal/aup">Acceptable Use Policy</a>. We may
        suspend or terminate workspaces that materially violate it.
      </p>
    ),
  },
  {
    heading: 'Availability and support',
    body: (
      <p>
        We aim for high availability but the Service is provided "as is" and "as available" without
        a guaranteed uptime level. Support is available via the in-app support module and the{' '}
        <a className="underline underline-offset-2" href="/contact">contact form</a>; we usually respond
        within one business day.
      </p>
    ),
  },
  {
    heading: 'Intellectual property',
    body: (
      <p>
        We own the Service, its software, and branding. You receive a limited, non-exclusive,
        non-transferable right to use the Service for your internal business operations during your
        subscription. Feedback you give us may be used without obligation.
      </p>
    ),
  },
  {
    heading: 'Termination',
    body: (
      <p>
        You can stop using the Service and cancel at any time from the billing page; cancellation
        takes effect at the end of the current billing cycle. When a workspace is deleted we retain its
        data for a 30-day grace window for recovery, then purge it from production systems in the
        ordinary course of operations (rolling database backups may retain it for up to 14 days).
        We may terminate or suspend access for material breach of these Terms.
      </p>
    ),
  },
  {
    heading: 'Disclaimers and liability',
    body: (
      <>
        <p>
          To the maximum extent permitted by law, we disclaim all implied warranties, and our total
          aggregate liability arising out of or relating to the Service is limited to the amounts
          you paid us in the 12 months before the event giving rise to the claim. We are not liable
          for indirect, incidental, special or consequential damages, or for loss of profits, data
          or goodwill.
        </p>
        <p>
          The Service produces business documents (invoices, payslips, reports) from the data you
          provide; you remain responsible for their accuracy and for your own tax, accounting and
          employment-law compliance.
        </p>
      </>
    ),
  },
  {
    heading: 'Changes to these Terms',
    body: (
      <p>
        We may update these Terms from time to time. Material changes will be announced by email or
        in-app at least 14 days before they take effect. Continuing to use the Service after a change
        becomes effective constitutes acceptance.
      </p>
    ),
  },
];

export function TermsPage() {
  return (
    <LegalPageLayout
      eyebrow="Legal"
      title="Terms of service"
      description="The terms that govern use of the Worqly platform."
      updated="June 4, 2026"
      sections={SECTIONS}
    />
  );
}
